import type { CampaignPlacement, FloorplanZone, StoreImage } from '../types';

/** Distinct outline colours for floorplan zone boxes — matched in the legend. */
export const ZONE_OUTLINE_PALETTE = [
  '#0284c7',
  '#7c3aed',
  '#d97706',
  '#059669',
  '#dc2626',
  '#db2777',
  '#0891b2',
  '#4f46e5',
  '#ca8a04',
  '#16a34a',
  '#ea580c',
  '#9333ea',
] as const;

export function zoneOutlineColor(index: number): string {
  return ZONE_OUTLINE_PALETTE[index % ZONE_OUTLINE_PALETTE.length]!;
}

export function buildZoneByInstoreLocationId(zones: FloorplanZone[]): Map<number, FloorplanZone> {
  const map = new Map<number, FloorplanZone>();
  for (const zone of zones) {
    if (zone.instoreLocationId != null) {
      map.set(zone.instoreLocationId, zone);
    }
  }
  return map;
}

export function buildZoneOutlineColors(zones: FloorplanZone[]): Record<string, string> {
  const colors: Record<string, string> = {};
  zones.forEach((zone, index) => {
    colors[zone.id] = zone.outlineColor ?? zoneOutlineColor(index);
  });
  return colors;
}

export function resolveZoneIdForInstoreLocationIds(
  instoreLocationIds: number[],
  zoneByInstoreLocationId: Map<number, FloorplanZone>,
): string | undefined {
  for (const id of instoreLocationIds) {
    const zone = zoneByInstoreLocationId.get(id);
    if (zone) return zone.id;
  }
  return undefined;
}

export interface InstoreLocationCampaignGroup {
  instoreLocationId: number;
  locationName: string;
  floorplanZoneId: string | null;
  outlineColor: string | null;
  campaigns: CampaignPlacement[];
}

/**
 * Campaigns grouped by posting-instruction in-store location.
 * Only includes placements that carry an instoreLocationId.
 */
export function groupCampaignsByInstoreLocation(
  campaigns: CampaignPlacement[],
  zones: FloorplanZone[],
  locationNameById: Map<number, string>,
): InstoreLocationCampaignGroup[] {
  const zoneByLocationId = buildZoneByInstoreLocationId(zones);
  const zoneIndexById = new Map(zones.map((zone, index) => [zone.id, index]));
  const groups = new Map<number, CampaignPlacement[]>();

  for (const campaign of campaigns) {
    if (campaign.instoreLocationId == null) continue;
    const list = groups.get(campaign.instoreLocationId) ?? [];
    list.push(campaign);
    groups.set(campaign.instoreLocationId, list);
  }

  return [...groups.entries()]
    .map(([instoreLocationId, groupCampaigns]) => {
      const zone = zoneByLocationId.get(instoreLocationId) ?? null;
      const outlineColor = zone
        ? zone.outlineColor ?? zoneOutlineColor(zoneIndexById.get(zone.id) ?? 0)
        : null;
      return {
        instoreLocationId,
        locationName: locationNameById.get(instoreLocationId)
          ?? groupCampaigns[0]?.instoreLocationName
          ?? `Location #${instoreLocationId}`,
        floorplanZoneId: zone?.id ?? groupCampaigns[0]?.floorplanZoneId ?? null,
        outlineColor,
        campaigns: groupCampaigns.sort((a, b) => b.installationDate.localeCompare(a.installationDate)),
      };
    })
    .sort((a, b) => a.locationName.localeCompare(b.locationName));
}

/** Retailer locations used in campaigns but not yet drawn on the floorplan. */
export function locationsMissingFloorplanBox(
  groups: InstoreLocationCampaignGroup[],
): InstoreLocationCampaignGroup[] {
  return groups.filter((group) => group.floorplanZoneId == null);
}

export function imageMatchesInstoreLocation(
  image: StoreImage,
  instoreLocationId: number,
  floorplanZoneId: string | null,
): boolean {
  if (image.hidden) return false;
  if (image.instoreLocationIds?.includes(instoreLocationId)) return true;
  if (image.postingInstructionInstoreLocationId === instoreLocationId) return true;
  if (image.instoreLocationId === instoreLocationId) return true;
  if (floorplanZoneId != null && image.floorplanZoneId === floorplanZoneId) return true;
  return false;
}

/**
 * Campaign proof and posting-instruction images for an in-store location / floorplan zone.
 */
export function getInstallImagesForInstoreLocation(
  images: StoreImage[],
  instoreLocationId: number,
  floorplanZoneId: string | null,
  campaigns: CampaignPlacement[],
): StoreImage[] {
  const seen = new Set<string>();
  const results: StoreImage[] = [];
  const campaignNames = campaigns.map((campaign) => campaign.campaignName.toLowerCase());
  const campaignTaskIds = new Set(
    campaigns
      .map((campaign) => campaign.campaignTaskId)
      .filter((id): id is number => id != null),
  );
  const campaignIds = new Set(campaigns.map((campaign) => campaign.campaignId));

  for (const image of images) {
    if (!image.url || image.hidden) continue;
    const matchesLocation = imageMatchesInstoreLocation(image, instoreLocationId, floorplanZoneId);
    const matchesTask = image.assignmentId != null && campaignTaskIds.has(image.assignmentId);
    const captionLower = image.caption.toLowerCase();
    const matchesCampaignName = campaignNames.some(
      (name) => name.length > 0 && captionLower.includes(name),
    );
    const matchesCampaignId = image.campaignId != null
      && campaignIds.has(String(image.campaignId))
      && (matchesLocation || matchesCampaignName || campaignTaskIds.size === 0);
    if (!matchesLocation && !matchesTask && !matchesCampaignId && !matchesCampaignName) continue;
    if (seen.has(image.url)) continue;
    seen.add(image.url);
    results.push(image);
  }

  for (const campaign of campaigns) {
    for (const url of campaign.installationImageUrls) {
      if (!url || seen.has(url)) continue;
      seen.add(url);
      results.push({
        id: `campaign-evidence-${campaign.id}-${url}`,
        url,
        caption: `${campaign.campaignName} — ${campaign.formatLabel}`,
        captureDate: campaign.installationDate,
        surveyId: 'campaign-evidence',
        zone: 'media_location',
        tags: ['install', campaign.brand, campaign.formatLabel],
        capturedBy: 'Field operator',
      });
    }
  }

  return results.sort((a, b) => b.captureDate.localeCompare(a.captureDate));
}
